import { Injector, Injectable, Inject } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  forkJoin,
  Observable,
  of,
  ReplaySubject,
  Subject,
  throwError,
} from "rxjs";
import { catchError, exhaustMap, map, pluck, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { SocketMessagesService } from "../core/socket/socket.service";
import { IUser, SettingsService } from "./settings/settings.service";
import { AuthService } from "../core/auth.service";
import {
  IAnalytics,
  IBillingEntity,
  IBooking,
  IBookingAudioInput,
  IBookingRecurrence,
  ICountry,
  IOrganization,
  IRecurrenceCalculate,
  ITimeZone,
  IWorkZone,
} from "../types";
import { DateTime } from "luxon";
import { ISessionDate, BookingsService } from "./bookings.service";
import { CustomErrorHandlerService } from "../core/custom-error-handler.service";
import uniq from "lodash/uniq";
import moment from "moment";
import { IBetaFeature } from "./settings/tools/beta-features/beta-features.component";
import orderBy from "lodash/orderBy";
import { ConfirmationService, IConfirmation } from "./confirmation.service";

interface IInternalTimeZone {
  time_zone: string;
  country?: string;
}

interface IRecurrenceCalculateResponse {
  time_zone: string;
  new_session_dates: ISessionDate[];
  destination_language: string;
  source_language: string;
}

export interface IUndelete {
  undelete: boolean;
  type: string;
  org_id?: string;
  billing_entity_id?: string;
  user_id?: string;
  group_id?: string;
}

export interface IMaintenance {
  id?: number;
  user_id?: number;
  region_id?: number;
  start_date?: string;
  end_date?: string;
  timezone?: string;
  description?: string;
  active?: number;
  contact?: string;
}

export interface IMaintenanceAffectedBooking {
  session_id: number;
  live_booking_id: number;
  start_epoch: number;
  end_epoch: number;
  title: string;
  created_by_user_id: number;
  created_by_email?: string;
  created_by_name: string;
}

export const priority_languages = [
  "Arabic",
  "Chinese (Simplified)",
  "Chinese (Traditional)",
  "Dutch",
  "English",
  "French",
  "German",
  "Italian",
  "Japanese",
  "Korean",
  "Portuguese",
  "Russian",
  "Spanish",
];

const CHINESE_SIMPLIFIED = "Chinese (Simplified)";
const CHINESE_TRADITIONAL = "Chinese (Traditional)";
const PORTUGUESE = "Portuguese";

function dedupeCsvLanguageParts(parts: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    const p = raw.trim();
    // istanbul ignore next -- both call sites below pass already-trimmed, already
    // Boolean-filtered parts, so `p` can never be empty here.
    if (!p) continue;
    const k = p.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

/** Drop Mandarin, add simplified + traditional once each; drop Brazilian Portuguese, add Portuguese once; dedupe case-insensitively (billing / booking CSV fields). */
export function replaceMandarinInCommaSeparatedLanguages(csv: string | undefined | null): string | undefined {
  if (csv == null) return undefined;
  const trimmed = csv.trim();
  if (!trimmed) return csv;
  const parts = trimmed
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const isMandarin = (p: string) => /^mandarin$/i.test(p);
  const isBrazilianPortuguese = (p: string) => /^brazilian portuguese$/i.test(p);
  const hadMandarin = parts.some(isMandarin);
  const hadBrazilianPortuguese = parts.some(isBrazilianPortuguese);
  let rest = parts.filter((p) => !isMandarin(p) && !isBrazilianPortuguese(p));
  rest = dedupeCsvLanguageParts(rest);
  if (hadMandarin) {
    if (!rest.some((p) => p === CHINESE_SIMPLIFIED)) rest.push(CHINESE_SIMPLIFIED);
    if (!rest.some((p) => p === CHINESE_TRADITIONAL)) rest.push(CHINESE_TRADITIONAL);
  }
  if (hadBrazilianPortuguese && !rest.some((p) => p === PORTUGUESE)) rest.push(PORTUGUESE);
  if (hadMandarin || hadBrazilianPortuguese) rest = dedupeCsvLanguageParts(rest);
  return rest.join(", ");
}

function dedupeLanguageListItemsByValue(items: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  let dividerKept = false;
  for (const l of items) {
    if (!l) continue;
    if (l.is_divider) {
      if (!dividerKept) {
        out.push(l);
        dividerKept = true;
      }
      continue;
    }
    const v = String(l.value ?? l.language ?? "").trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ ...l, value: v, language: l.language ?? v });
  }
  return out;
}

/** API language rows: remove Mandarin/Brazilian Portuguese, add Chinese variants / Portuguese if missing, dedupe by display value. */
export function replaceMandarinInLanguageListItems(items: any[]): any[] {
  if (!Array.isArray(items) || !items.length) return items;
  const nameOf = (l: any) => String(l?.value ?? l?.language ?? "").trim();
  const isMandarin = (l: any) => /^mandarin$/i.test(nameOf(l));
  const isBrazilianPortuguese = (l: any) => /^brazilian portuguese$/i.test(nameOf(l));
  const hadMandarin = items.some(isMandarin);
  const hadBrazilianPortuguese = items.some(isBrazilianPortuguese);
  const mandarinTemplate = items.find(isMandarin) || {};
  const brPortugueseTemplate = items.find(isBrazilianPortuguese) || {};
  let working = items.filter((l) => !isMandarin(l) && !isBrazilianPortuguese(l));
  if (hadMandarin) {
    if (!working.some((l) => String(l.value).trim() === CHINESE_SIMPLIFIED)) {
      working.push({ ...mandarinTemplate, language: CHINESE_SIMPLIFIED, value: CHINESE_SIMPLIFIED });
    }
    if (!working.some((l) => String(l.value).trim() === CHINESE_TRADITIONAL)) {
      working.push({ ...mandarinTemplate, language: CHINESE_TRADITIONAL, value: CHINESE_TRADITIONAL });
    }
  }
  if (hadBrazilianPortuguese && !working.some((l) => String(l.value).trim() === PORTUGUESE)) {
    working.push({ ...brPortugueseTemplate, language: PORTUGUESE, value: PORTUGUESE });
  }
  return dedupeLanguageListItemsByValue(working);
}

/** Map legacy { Mandarin: true } / { "Brazilian Portuguese": true } to their new keys when present in the billing language list. */
export function migrateMandarinInBooleanLanguageMap(
  value: Record<string, boolean> | null | undefined,
  availableCommaSeparated: string,
): Record<string, boolean> | null | undefined {
  if (!value || typeof value !== "object") return value;
  const available = new Set(
    availableCommaSeparated
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );
  const mandarinKey = Object.keys(value).find((k) => /^mandarin$/i.test(k.trim()));
  const brPortugueseKey = Object.keys(value).find((k) => /^brazilian portuguese$/i.test(k.trim()));
  if ((!mandarinKey || !value[mandarinKey]) && (!brPortugueseKey || !value[brPortugueseKey])) return value;
  const next: Record<string, boolean> = { ...value };
  if (mandarinKey && value[mandarinKey]) {
    delete next[mandarinKey];
    if (available.has(CHINESE_SIMPLIFIED)) next[CHINESE_SIMPLIFIED] = true;
    if (available.has(CHINESE_TRADITIONAL)) next[CHINESE_TRADITIONAL] = true;
  }
  if (brPortugueseKey && value[brPortugueseKey]) {
    delete next[brPortugueseKey];
    if (available.has(PORTUGUESE)) next[PORTUGUESE] = true;
  }
  return next;
}

@Injectable({
  providedIn: "root",
})
export class CommonService {
  public reloadSearch$: Subject<any> = new Subject<any>();
  private getTimeZones$$: Subject<void> = new Subject<void>();
  private timeZones$$: ReplaySubject<ITimeZone[]> = new ReplaySubject<ITimeZone[]>(1);

  private getCountries$$: Subject<void> = new Subject<void>();
  private countries$$: ReplaySubject<ICountry[]> = new ReplaySubject<ICountry[]>(1);

  private currencies$$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private currencies$ = this.currencies$$.asObservable().pipe(
    switchMap((data) => {
      if (!data.length) {
        // @ts-ignore
        return this.messages.request$("getCurrencyCodes").pipe(
          pluck("currencies"),
          map((c: any) => c.map((_: any) => _.iso_code)),
        );
      } else {
        return of(data);
      }
    }),
    shareReplay(1),
  );
  private users$$: BehaviorSubject<IUser[]> = new BehaviorSubject<IUser[]>([]);
  public organizations$$: BehaviorSubject<IOrganization[]> = new BehaviorSubject<IOrganization[]>([]);
  public billingEntities$$: BehaviorSubject<IBillingEntity[]> = new BehaviorSubject<IBillingEntity[]>([]);
  private usersBECache: Map<number, IUser[]> = new Map<number, IUser[]>();
  public caption_list!: any;
  public recurrence_dates: { date: string; day: string }[] = [];
  public notifications = [];
  public series_summary = [];
  public machine_languages: any[] = [];
  public beta_features$: Subject<IBetaFeature[]> = new Subject<IBetaFeature[]>();
  public show_announcement_bar$: Subject<boolean> = new Subject<boolean>();
  injector: Injector;

  constructor(
    private messages: SocketMessagesService,
    private auth: AuthService,
    private errors: CustomErrorHandlerService,
    private settings: SettingsService,
    private confirmation: ConfirmationService,
    @Inject(Injector) injector: Injector,
  ) {
    this.injector = injector;
    this.getTimeZones$$
      .asObservable()
      .pipe(
        // @ts-ignore
        exhaustMap(() =>
          this.messages.request$("getTimeZones").pipe(
            pluck("time_zones"),
            map((zones: any) => {
              let usTZ = zones
                .filter((_: any) => {
                  return _.country === "US";
                })
                .map((_: any) => {
                  const __ = DateTime.local().setZone(_.time_zone);
                  return Object.assign({}, _, {
                    title: [__.offsetNameShort, __.offsetNameLong].join("/"),
                    group: "US",
                  }) as ITimeZone;
                });

              usTZ = Array.from(new Set(usTZ.map((a: any) => a.title))).map((title) => {
                return usTZ.find((a: any) => a.title === title);
              }) as ITimeZone[];

              usTZ.sort((a: any, b: any) => a.title.localeCompare(b.title));

              return usTZ
                .concat(
                  zones.map(
                    (_: any) =>
                      Object.assign({}, _, {
                        group: "IANA",
                        title: _.time_zone.replace("_", " "),
                      }) as ITimeZone,
                  ),
                )
                .map((_: any) => {
                  const __ = DateTime.local().setZone(_.time_zone);
                  const isPositive = __.offset >= 0;
                  const minutes = Math.abs(__.offset);
                  const offsetString =
                    (isPositive ? "+" : "-") + DateTime.now().startOf("day").plus({ minutes }).toFormat("HH:mm");

                  return Object.assign({}, _, {
                    utc: "UTC" + offsetString,
                    title: _.title + " (UTC" + offsetString + ")",
                  });
                });
            }),

            catchError((err) => {
              this.errors.handleError(err, "@common.service1");
              return EMPTY;
            }),
          ),
        ),
        take(1),
      )
      .subscribe(this.timeZones$$ as any);
    this.getCountries$$
      .asObservable()
      .pipe(
        // @ts-ignore
        switchMap(() =>
          this.messages.request$("getCountryCodes").pipe(
            pluck("countries"),
            catchError((err) => {
              this.errors.handleError(err, "@common.service2");
              return EMPTY;
            }),
          ),
        ),
        take(1),
      )
      .subscribe(this.countries$$ as any);
  }

  getMachineLanguages$() {
    return this.messages.request$("getMachineLanguages").pipe(
      tap((ml: any) => {
        let new_ml = replaceMandarinInLanguageListItems(ml?.map((l: any) => ({ ...l, value: l?.language })) || []);
        const priority = orderBy(
          new_ml.filter((l: any) => priority_languages.includes(l.value)),
          ["value"],
        );
        const others = new_ml.filter((l: any) => !priority_languages.includes(l.value));
        new_ml = dedupeLanguageListItemsByValue([...priority, { value: "----------", is_divider: true }, ...others]);
        this.machine_languages = new_ml;
      }),
    );
  }

  getBillingEntities$(org_id?: number) {
    return this.settings.getBillingEntities$(org_id);
  }

  getBeDetails$(be_id: number, org_id: number) {
    return combineLatest([
      this.settings.getBillingEntities$(),
      this.getOrganizationsMap$().pipe(map((organizations) => Array.from(organizations.values()))),
    ]).pipe(
      map(([entities, orgs]) => {
        const new_entities = entities.filter((entity) => entity.billing_entity_id === be_id);
        const new_orgs = orgs.filter((org) => org.org_id === org_id);

        return new_entities.map((entity) => {
          const e = new_orgs.filter((_: IOrganization) => _.org_id === entity.org_id);
          if (e.length) {
            const name = e.pop();
            if (name) {
              Object.assign(entity, { organization: name.org_name });
            }
          }
          return entity;
        });
      }),
    );
  }

  getNotifications$() {
    // @ts-ignore
    return this.messages.request$("getNotifications").pipe(
      tap((notifications: any) => {
        this.notifications = notifications;
        this.getSeriesSummary$().subscribe();
      }),
    );
  }

  getSeriesSummary$() {
    // @ts-ignore
    return this.messages.request$("getSeriesSummary").pipe(
      tap((series_summary: any) => {
        this.series_summary = series_summary;
      }),
    );
  }

  updateNotifications$(id: number, message?: string, type?: string, unread?: number, metadata?: any) {
    // @ts-ignore
    return this.messages.request$("updateNotifications", { id, message, type, unread, metadata }).pipe();
  }

  getWorkZones$(): Observable<IWorkZone[]> {
    // @ts-ignore
    return this.messages.request$("getWorkZones").pipe(
      pluck("work_zones"),
      catchError((err) => {
        this.errors.handleError(err, "@common.service3");
        return EMPTY;
      }),
    );
  }

  getTimeZones$(): Observable<ITimeZone[]> {
    this.getTimeZones$$.next();
    return this.timeZones$$.asObservable();
  }

  getCountries$(): Observable<ICountry[]> {
    this.getCountries$$.next();
    return this.countries$$.asObservable();
  }

  getUsersMap$(): Observable<Map<number, IUser>> {
    return this.users$$.asObservable().pipe(
      map((_) => _ as IUser[]),
      map(
        (users) =>
          new Map(
            users.map((user) => {
              return [user.user_id as number, user];
            }),
          ),
      ),
    );
  }

  getBEUsers$(be: number): Observable<IUser[]> {
    if (this.usersBECache.has(be)) {
      return of(this.usersBECache.get(be) as IUser[]);
    } else {
      // @ts-ignore
      return this.messages
        .request$("getUsers", {
          billing_entity_id: be,
        })
        .pipe(
          pluck("users"),
          tap((users) => this.usersBECache.set(be, users as any)),
          catchError((err) => {
            this.errors.handleError(err, "@common.service4");
            return EMPTY;
          }),
        );
    }
  }

  getCurrencies$(): Observable<string[]> {
    return this.currencies$ as Observable<string[]>;
  }

  getOrganizationsMap$(): Observable<Map<number, IOrganization>> {
    return this.organizations$$.asObservable().pipe(
      map(
        (organizations) =>
          new Map(
            organizations.map((org) => {
              return [org.org_id as number, org];
            }),
          ),
      ),
    );
  }

  getViewerUsers(session_guid: string): Observable<any> {
    // @ts-ignore
    return this.messages.request$("getSessionViewers", { session_guid }).pipe();
  }

  getBillingEntitiesMap$(): Observable<Map<number, IBillingEntity>> {
    return this.billingEntities$$.asObservable().pipe(
      map((users) => {
        return new Map(
          users.map((billingEntity) => {
            return [billingEntity.billing_entity_id, billingEntity];
          }),
        );
      }),
    );
  }

  /**
   * called on app init or when user list was changed
   * @param users - when called from user editor - we do not need to get users from server again - so we send user list
   */
  public reloadUsers(users?: IUser[]): void {
    if (!users) {
      this.getBEUsers$(this.auth.getUserBillingEntityId())
        .pipe(
          tap((data) => this.users$$.next(data)),
          take(1),
        )
        .subscribe();
    } else {
      this.users$$.next(users);
    }
  }

  /**
   * called on app init or when billing list was changed
   * @param entities - when called from user editor - we do not need to get users from server again - so we send user list
   */
  public reloadBillingEntities(entities?: IBillingEntity[]): void {
    if (!entities) {
      this.messages
        // @ts-ignore
        .request$("getBillingEntities")
        .pipe(
          pluck("billing_entities"),
          tap((data: any) => {
            this.auth.be_list = data as IBillingEntity[];
            this.auth.org_srv_type_list = data?.reduce(
              (acc: { [key: string]: number[] }, i: IBillingEntity) => ({
                ...acc,
                [i.org_id]: acc[i.org_id] ? uniq([...acc[i.org_id], ...i.service_types]) : i.service_types,
              }),
              {},
            ) as { [key: string]: number[] };
            this.billingEntities$$.next(data as IBillingEntity[]);
          }),
          take(1),
        )
        .subscribe();
    } else {
      this.billingEntities$$.next(entities);
    }
  }

  public reloadOrganizations(organizations?: IOrganization[]): void {
    if (!organizations) {
      this.messages
        // @ts-ignore
        .request$("getOrganisations")
        .pipe(
          pluck("organisations"),
          tap((data) => {
            this.auth.org_list = data as unknown as IOrganization[];
            this.organizations$$.next(data as IOrganization[]);
          }),
          map((_: any) => {
            return _.map((org: any) => {
              const { org_id, org_name, events_can_be_created, classes_can_be_created, meetings_can_be_created } = org;
              return {
                org_id,
                org_name,
                events_can_be_created: Boolean(events_can_be_created),
                classes_can_be_created: Boolean(classes_can_be_created),
                meetings_can_be_created: Boolean(meetings_can_be_created),
              };
            });
          }),
          take(1),
        )
        .subscribe();
    } else {
      this.organizations$$.next(organizations);
    }
  }

  setCaptionList(list: any) {
    this.caption_list = list;
  }

  calcSessionRecurrences$(data: IRecurrenceCalculate): Observable<IRecurrenceCalculateResponse> {
    (Object.keys(data.recurrence) as Array<keyof IBookingRecurrence>).forEach((key) => {
      if (data.recurrence[key] === null) {
        delete data.recurrence[key];
      }
    });
    // @ts-ignore
    return this.messages.request$("calcSessionRecurrences", data).pipe(
      tap(({ new_session_dates }: any) => {
        this.recurrence_dates = new_session_dates?.map(({ date }: { date: string }) => ({
          date,
          day: moment(date).format("dddd"),
        }));
      }),
      catchError((err) => {
        console.error(err);
        return EMPTY;
      }),
    );
  }

  public searchOrganisations$(search: { org_name: string; deleted: number }) {
    return this.messages.request$("getOrganisations", { search });
  }

  public searchBillingEntities(search: { name: string; deleted: number }) {
    return this.messages.request$("getBillingEntities", { search });
  }

  public searchUsers$(billing_entity_id: number, search: { id: string; deleted: number }) {
    const data = { billing_entity_id, search };
    return this.messages.request$("getUsers", data);
  }

  public searchGroups$(billing_entity_id: number, search: { group_name: string; deleted: number }) {
    const data = { billing_entity_id, search };
    return this.messages.request$("getGroups", data);
  }

  public unDeleteEntities(data: IUndelete): Observable<unknown> {
    const { type, org_id, billing_entity_id, user_id, group_id } = data;

    if (type === "org") {
      return this.messages.request$("undeleteOrganisation", { org_id });
    }

    if (type === "be") {
      return this.messages.request$("undeleteBillingEntity", { billing_entity_id });
    }

    if (type === "user") {
      return this.messages.request$("undeleteUser", { user_id });
    }

    if (type === "group") {
      return this.messages.request$("undeleteGroup", { group_id });
    }

    return throwError(() => new Error(`unDeleteEntities: unsupported type "${type}"`));
  }

  public setAnalytics$(analytics: IAnalytics) {
    return this.messages.request$("createAnalytics", analytics);
  }

  getBetaFeatures$ = () => {
    return this.messages.request$("getBetaFeatures").pipe(
      tap((data: IBetaFeature[]) => {
        this.beta_features$.next(data);
      }),
    );
  };

  public getMaintenance$(): Observable<IMaintenance[]> {
    return this.messages.request$("getMaintenance").pipe(map((response: any) => response || []));
  }

  public createMaintenance$(maintenance: IMaintenance): Observable<any> {
    return this.messages.request$("createMaintenance", maintenance);
  }

  public updateMaintenance$(maintenance: IMaintenance): Observable<any> {
    return this.messages.request$("updateMaintenance", maintenance);
  }

  public notifyMaintenanceAffectedBookings$(id: number): Observable<any> {
    return this.messages.request$("notifyMaintenanceAffectedBookings", { id });
  }

  // === MAINTENANCE CONFLICT CHECKING METHODS ===

  /**
   * Check if a booking conflicts with maintenance windows
   * @param startDate - Booking start date (YYYY-MM-DD)
   * @param startTime - Booking start time (HH:mm)
   * @param endTime - Booking end time (HH:mm)
   * @param selectedServiceType - Service type selected
   * @param captions - Selected captions
   * @param billingEntityId - Optional billing entity ID (for admin users booking for other orgs)
   * @returns Observable with conflict result
   */
  public checkMaintenanceConflict$(
    startDate: string,
    startTime: string,
    endTime: string,
    timeZone: string,
    selectedServiceType: string,
    captions: any[],
    billingEntityId?: number,
  ): Observable<{ hasConflict: boolean; conflictingMaintenance?: IMaintenance; message?: string }> {
    return this.getMaintenance$().pipe(
      switchMap((maintenanceData: IMaintenance[]) => {
        // Only check if maintenance data is loaded and there are active maintenance windows
        if (!maintenanceData || maintenanceData.length === 0) {
          return of({ hasConflict: false });
        }

        // Check if this booking uses Ai-Live services
        if (!this.isAiLiveBooking(selectedServiceType, captions)) {
          return of({ hasConflict: false });
        }

        // Get user's region (use billing entity ID if provided)
        return this.getUserRegion$(billingEntityId).pipe(
          switchMap((userRegion) => {
            if (!userRegion) {
              return of({ hasConflict: false });
            }

            // Check each active maintenance window
            const activeMaintenance = maintenanceData.filter((m) => m.active === 1);

            for (const maintenance of activeMaintenance) {
              // Only check maintenance windows for the user's region
              if (maintenance.region_id !== userRegion) {
                continue;
              }

              if (this.hasTimeConflict(startDate, startTime, endTime, timeZone, maintenance)) {
                return of({
                  hasConflict: true,
                  conflictingMaintenance: maintenance,
                  message: "Maintenance conflict detected",
                });
              }
            }

            return of({ hasConflict: false });
          }),
        );
      }),
      catchError(() => of({ hasConflict: false })),
    );
  }

  /**
   * Check for maintenance conflicts and show dialog if needed (for regular bookings)
   * @param startDate - Booking start date
   * @param startTime - Booking start time
   * @param endTime - Booking end time
   * @param selectedServiceType - Service type
   * @param captions - Selected captions
   * @param billingEntityId - Optional billing entity ID (for admin users booking for other orgs)
   * @returns Observable<boolean> - true if can proceed, false if blocked
   */
  public checkAndHandleMaintenanceConflict$(
    startDate: string,
    startTime: string,
    endTime: string,
    timeZone: string,
    selectedServiceType: string,
    captions: any[],
    billingEntityId?: number,
  ): Observable<boolean> {
    return this.checkMaintenanceConflict$(
      startDate,
      startTime,
      endTime,
      timeZone,
      selectedServiceType,
      captions,
      billingEntityId,
    ).pipe(
      switchMap((conflict) => {
        if (conflict.hasConflict && conflict.conflictingMaintenance) {
          return this.showMaintenanceDialog$(conflict.conflictingMaintenance).pipe(
            map(() => false), // Always return false when there's a conflict
          );
        }
        return of(true); // No conflict, booking can proceed
      }),
    );
  }

  /**
   * Check for Instant LEXI maintenance conflicts
   * @param durationMins - Duration in minutes
   * @param billingEntityId - Optional billing entity ID (for admin users booking for other orgs)
   * @returns Observable<boolean> - true if can proceed, false if blocked
   */
  public checkInstantLexiMaintenanceConflict$(durationMins: number, billingEntityId?: number): Observable<boolean> {
    return this.getMaintenance$().pipe(
      switchMap((maintenanceData: IMaintenance[]) => {
        // Get current time and calculate session end time
        const sessionStart = moment();
        const sessionEnd = moment().add(durationMins, "minutes");

        // Get user's region (use billing entity ID if provided)
        return this.getUserRegion$(billingEntityId).pipe(
          switchMap((userRegion) => {
            if (!userRegion) {
              return of(true); // No region info, allow session
            }

            // Check active maintenance windows for user's region
            const activeMaintenance = maintenanceData.filter((m) => m.active === 1 && m.region_id === userRegion);

            // Check each maintenance window for conflicts
            for (const maintenance of activeMaintenance) {
              if (this.hasInstantLexiTimeConflict(sessionStart, sessionEnd, maintenance)) {
                // Show maintenance dialog and block session
                return this.showMaintenanceDialog$(maintenance).pipe(
                  map(() => false), // Always return false when there's a conflict
                );
              }
            }

            return of(true); // No conflicts, allow session
          }),
        );
      }),
      catchError(() => of(true)), // On error, allow session to proceed
    );
  }

  /**
   * Check if the current booking uses Ai-Live services
   */
  private isAiLiveBooking(selectedServiceType: string, captions: any[]): boolean {
    // Instant LEXI bookings are always affected by AI-Live maintenance
    if (selectedServiceType === "Instant LEXI") {
      return true;
    }

    const aiLiveServices = ["ai-live lexi", "lexi access", "remote captioning", "google event - remote captioning"];
    if (!aiLiveServices.includes(selectedServiceType.toLowerCase()) || !captions?.length) return false;

    return captions.some((caption) => {
      const name = caption.caption_output_name?.toLowerCase() || "";
      return name.includes("ai-live") || name.includes("ai-live lexi") || name.includes("zoom + ai-live");
    });
  }

  /**
   * Get the user's region ID
   * @param billingEntityId - Optional specific billing entity ID to get region for
   * @returns Observable<number | null> - The region ID for the billing entity
   */
  private getUserRegion$(billingEntityId?: number): Observable<number | null> {
    return billingEntityId
      ? this.getBillingEntitiesMap$().pipe(
          map((beMap) => {
            return beMap.get(billingEntityId)?.region_id || null;
          }),
        )
      : of(this.auth.cached_billing_entity?.region_id || null);
  }

  /**
   * Check if time period conflicts with maintenance window (including 15-minute buffer)
   */
  private hasTimeConflict(
    startDate: string,
    startTime: string,
    endTime: string,
    timeZone: string,
    maintenance: IMaintenance,
  ): boolean {
    if (!startDate || !startTime || !endTime) return false;

    try {
      const zone = timeZone || "UTC";
      const startLuxon = DateTime.fromFormat(`${startDate} ${startTime}`, "yyyy-MM-dd H:m", { zone });
      const endLuxon = DateTime.fromFormat(`${startDate} ${endTime}`, "yyyy-MM-dd H:m", { zone });
      if (!startLuxon.isValid || !endLuxon.isValid) return false;
      const start = moment(startLuxon.toUTC().toISO());
      const end = moment(endLuxon.toUTC().toISO());
      return this.checkTimeOverlap(start, end, maintenance);
    } catch {
      return false;
    }
  }

  /**
   * Check if Instant LEXI session conflicts with maintenance window
   */
  private hasInstantLexiTimeConflict(
    sessionStart: moment.Moment,
    sessionEnd: moment.Moment,
    maintenance: IMaintenance,
  ): boolean {
    try {
      return this.checkTimeOverlap(sessionStart, sessionEnd, maintenance);
    } catch {
      return false;
    }
  }

  /**
   * Common time overlap logic with 15-minute buffer
   */
  private checkTimeOverlap(start: moment.Moment, end: moment.Moment, maintenance: IMaintenance): boolean {
    // Convert maintenance times to the same timezone as the booking times for proper comparison
    const maintenanceTimezone = maintenance.timezone || "UTC";
    const maintenanceEnd = moment(maintenance.end_date).tz(maintenanceTimezone);
    const bufferStart = moment(maintenance.start_date).tz(maintenanceTimezone).subtract(15, "minutes");

    // Ensure booking times are also in the same timezone
    const bookingStart = start.tz ? start.tz(maintenanceTimezone) : start;
    const bookingEnd = end.tz ? end.tz(maintenanceTimezone) : end;

    return bookingStart.isBefore(maintenanceEnd) && bookingEnd.isAfter(bufferStart);
  }

  /**
   * Show unified maintenance popup message for all booking types
   */
  private showMaintenanceDialog$(maintenance: IMaintenance): Observable<any> {
    const contactEmail = maintenance.contact || "urgent@ai-media.tv";
    const confirmationData: IConfirmation = {
      message: `Scheduled maintenance will be taking place for Ai-Live during the booking time you have selected. Please contact our Coordination team at ${contactEmail} with the details of the session you would like to book and they will place it on your behalf. We appreciate your patience while we update Ai-Live with the latest and greatest features!`,
      title: "Upcoming Site Maintenance",
      confirm: "OK",
    };
    return this.confirmation.confirm$(confirmationData);
  }

  // LBP-185: Check for overlapping bookings with same meeting URL
  public checkAndHandleMeetingUrlOverlap$(
    selectedServiceType: string,
    audioInput: IBookingAudioInput | undefined,
    startDate: string,
    startTime: string,
    endTime: string,
    timeZone: string,
    billingEntityId?: number,
    excludeBookingId?: number,
  ): Observable<boolean> {
    const bookings = this.injector.get(BookingsService);
    return bookings.checkAndHandleMeetingUrlOverlap$(
      selectedServiceType,
      audioInput,
      startDate,
      startTime,
      endTime,
      timeZone,
      billingEntityId,
      excludeBookingId,
    );
  }
}
